// Shown while the server has not yet confirmed who is signed in.
//
// It is the app shell drawn as skeletons — sidebar, topbar (with an avatar
// placeholder, never a name), content cards — so the hand-off to the real
// shell is seamless, plus a status pill in the same place pages show their
// own "Loading…" pill. Nothing here identifies a person.
import React from "react";
import { ShieldCheck } from "lucide-react";
import { useSessionUi, SessionStyles, NexployBrand, SESSION_SCREEN_Z, BRAND } from "./SessionUi";

const Bar = ({ w, h = 12, r = 8, style, className = "" }) => (
  <div className={`nx-session-skeleton ${className}`} style={{ width: w, height: h, borderRadius: r, ...style }} />
);

export default function VerifyingAccountScreen({ slow = false }) {
  const ui = useSessionUi();
  const { palette, t } = ui;

  return (
    <div
      dir={ui.dir}
      lang={ui.language}
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="nx-verify"
      style={{
        position: "fixed", inset: 0, zIndex: SESSION_SCREEN_Z,
        display: "flex", background: palette.page, color: palette.text, overflow: "hidden",
      }}
    >
      <SessionStyles palette={palette} />
      <style>{`
        .nx-verify__sidebar { width: 17.5rem; flex-shrink: 0; display: flex; flex-direction: column; gap: 22px;
          padding: 18px 16px; border-inline-end: 1px solid ${palette.border}; background: ${palette.chrome}; }
        .nx-verify__topbar { height: 4rem; margin: 0 1rem; padding: 0 1.25rem; display: flex; align-items: center;
          justify-content: space-between; gap: 12px; border: 1px solid ${palette.border}; border-top: none;
          border-radius: 0 0 0.875rem 0.875rem; background: ${palette.chrome}; }
        .nx-verify__brand-mobile { display: none; }
        .nx-verify__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
        @media (max-width: 1024px) { .nx-verify__sidebar { width: 4.5rem; padding: 18px 12px; }
          .nx-verify__nav-label, .nx-verify__brand-side { display: none; } }
        @media (max-width: 768px) {
          .nx-verify__sidebar { display: none; }
          .nx-verify__topbar { height: 3.5rem; margin: 0; padding: 0 0.75rem; border-radius: 0; border-inline: none; }
          .nx-verify__brand-mobile { display: flex; }
          .nx-verify__title-bar { display: none; }
          .nx-verify__grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Sidebar skeleton */}
      <aside className="nx-verify__sidebar" aria-hidden="true">
        <div className="nx-verify__brand-side">
          <NexployBrand theme={ui.theme} language={ui.language} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Bar w={28} h={28} r={8} />
              <Bar w={`${55 + ((i * 13) % 30)}%`} className="nx-verify__nav-label" />
            </div>
          ))}
        </div>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Topbar skeleton — avatar placeholder only, no name */}
        <div className="nx-verify__topbar" aria-hidden="true">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="nx-verify__brand-mobile">
              <NexployBrand theme={ui.theme} language={ui.language} />
            </div>
            <Bar w={120} h={14} className="nx-verify__title-bar" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Bar w={40} h={40} r={10} />
            <Bar w={40} h={40} r={10} />
            <Bar w={40} h={40} r={20} />
          </div>
        </div>

        <div style={{ flex: 1, overflow: "hidden", padding: "1.25rem clamp(12px, 3vw, 1.25rem)" }}>
          {/* Status pill — same placement as the pages' own loading pill */}
          <div style={{ display: "flex", justifyContent: "center", margin: "6px 0 22px" }}>
            <div
              style={{
                display: "inline-flex", alignItems: "center", gap: 12, maxWidth: "100%",
                padding: "12px 22px", borderRadius: 999,
                background: palette.floating, border: `1px solid ${palette.border}`,
              }}
            >
              <span
                className="nx-session-spin"
                aria-hidden="true"
                style={{
                  width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                  border: `2.5px solid ${BRAND}`, borderTopColor: "transparent",
                }}
              />
              <ShieldCheck size={18} color={palette.accentText} aria-hidden="true" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: palette.text }}>{t.verifyingTitle}</span>
            </div>
          </div>
          {slow && (
            <p style={{ margin: "-10px auto 22px", maxWidth: 420, textAlign: "center", fontSize: 13, lineHeight: 1.6, color: palette.textSecondary }}>
              {t.verifyingSlow}
            </p>
          )}

          {/* Page skeleton */}
          <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1100, margin: "0 auto" }}>
            <div
              style={{
                display: "flex", alignItems: "center", gap: 18, padding: 22,
                border: `1px solid ${palette.border}`, borderRadius: 16, background: palette.surface,
              }}
            >
              <Bar w={72} h={72} r={36} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                <Bar w="38%" h={20} />
                <Bar w="24%" />
                <Bar w="30%" />
              </div>
            </div>
            <div className="nx-verify__grid">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    padding: 18, border: `1px solid ${palette.border}`, borderRadius: 16,
                    background: palette.surface, display: "flex", flexDirection: "column", gap: 12,
                  }}
                >
                  <Bar w="45%" h={14} />
                  <Bar w="80%" />
                  <Bar w="65%" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
